import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChevronRight, ChevronLeft, LogOut, Loader2, AlertTriangle,
  Twitter, Youtube, Instagram, Facebook, Linkedin, Pin,
} from 'lucide-react';
import { fetchAnnouncement } from '@/utils/adminApi';
import { buildPosts, monthName } from '@/utils/announcementTemplates';
import { HUAWEI_URL, GOOGLE_URL } from '@/components/StoreDownloadSection';
import { ADMIN_COLORS as C } from '@/components/admin/AdminPage';
import { PostField, PlatformCard } from '@/components/admin/PostField';

const LANGS = [
  { code: 'ar', label: 'العربية', dir: 'rtl' },
  { code: 'en', label: 'English', dir: 'ltr' },
  { code: 'id', label: 'Indonesia', dir: 'ltr' },
  { code: 'ur', label: 'اردو', dir: 'rtl' },
  { code: 'tr', label: 'Türkçe', dir: 'ltr' },
];

const STATUS_AR = { easy: 'سهلة بالعين المجردة', possible: 'ممكنة إذا كانت السماء صافية', difficult: 'صعبة، قد تحتاج أدوات بصرية', not_visible: 'غير ممكنة في الأيام الأولى' };

const SummaryRow = ({ label, value, testId }) => (
  <div className="flex justify-between gap-3 py-1.5 text-sm" style={{ borderBottom: `1px solid ${C.border}` }}>
    <span style={{ color: C.muted }}>{label}</span>
    <span className="font-semibold text-left" data-testid={testId}>{value}</span>
  </div>
);

const Summary = ({ data }) => {
  const conj = new Date(data.conjunction_utc);
  const meccaFmt = new Intl.DateTimeFormat('ar-EG', { timeZone: 'Asia/Riyadh', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', numberingSystem: 'latn' });
  const warn = data.first_day_gregorian && data.visibility_date && data.first_day_gregorian <= data.visibility_date;
  const longAr = (ymd) => new Intl.DateTimeFormat('ar-EG', { timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', numberingSystem: 'latn' })
    .format(new Date(`${ymd}T12:00:00Z`));
  return (
    <div className="rounded-2xl p-4 mb-5" style={{ background: C.card, border: `1px solid ${C.border}` }} data-testid="announcement-summary">
      <SummaryRow label="الاقتران (بتوقيت مكة)" value={meccaFmt.format(conj)} testId="summary-conjunction" />
      <SummaryRow label="أول رؤية ممكنة في مكة" value={data.visibility_date ? `مساء ${longAr(data.visibility_date)} — غروب ${data.sunset_time}` : '—'} testId="summary-visibility" />
      <SummaryRow label="حالة الرؤية" value={STATUS_AR[data.visibility_status] || '—'} testId="summary-status" />
      <SummaryRow label="عمر القمر / ارتفاعه عند الغروب" value={data.moon_age_hours != null ? `${data.moon_age_hours} ساعة / ${data.moon_altitude}°` : '—'} testId="summary-moon" />
      <SummaryRow label="غرة الشهر في تقويم فلك" value={data.first_day_gregorian ? longAr(data.first_day_gregorian) : '—'} testId="summary-first-day" />
      {warn && (
        <div className="mt-3 flex gap-2 text-sm rounded-lg p-3" style={{ background: 'rgba(245,158,11,0.14)', border: '1px solid rgba(245,158,11,0.5)', color: '#FEF3C7' }} data-testid="summary-warning">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" color="#FBBF24" />
          في هذا الشهر يبدأ تقويم فلك الحسابي قبل الرؤية المتوقعة، لذلك تذكر النصوص التاريخين تلقائياً: غرة التقويم وغرة الرؤية المتوقعة.
        </div>
      )}
    </div>
  );
};

export const AnnouncementGenerator = ({ user, onLogout, onUnauthorized }) => {
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [lang, setLang] = useState('ar');

  const load = useCallback(async (o) => {
    setData(null);
    setError(null);
    try {
      setData(await fetchAnnouncement(o));
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      else setError('تعذّر تحميل البيانات، حاول مجدداً');
    }
  }, [onUnauthorized]);

  useEffect(() => { load(offset); }, [offset, load]);

  const links = useMemo(() => ({ about: `${window.location.origin}/about`, google: GOOGLE_URL, huawei: HUAWEI_URL }), []);
  const posts = useMemo(() => (data ? buildPosts(data, lang, links) : null), [data, lang, links]);
  const dir = LANGS.find((l) => l.code === lang).dir;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <header className="flex items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">مولّد إعلانات الهلال</h1>
          <p className="text-xs mt-1" style={{ color: C.muted }} dir="ltr">{user.email}</p>
        </div>
        <button type="button" onClick={onLogout} className="flex items-center gap-1.5 text-sm rounded-lg px-3 py-2"
          style={{ background: C.field, border: `1px solid ${C.border}` }} data-testid="admin-logout-btn">
          <LogOut size={15} /> خروج
        </button>
      </header>

      <div className="flex items-center justify-between rounded-2xl p-3 mb-4" style={{ background: C.card, border: `1px solid ${C.border}` }}>
        <button type="button" disabled={offset === 0} onClick={() => setOffset((o) => o - 1)} className="p-2 rounded-lg"
          style={{ opacity: offset === 0 ? 0.35 : 1, background: C.field }} aria-label="الشهر السابق" data-testid="month-prev-btn">
          <ChevronRight size={20} />
        </button>
        <div className="text-center" data-testid="month-label">
          <div className="text-lg font-bold">{data ? `${data.month_name_ar} ${data.hijri_year} هـ` : '...'}</div>
          <div className="text-xs" style={{ color: C.muted }}>{offset === 0 ? 'الهلال القادم' : offset === 1 ? 'بعد شهر' : offset === 2 ? 'بعد شهرين' : `بعد ${offset} أشهر`}</div>
        </div>
        <button type="button" disabled={offset === 11} onClick={() => setOffset((o) => o + 1)} className="p-2 rounded-lg"
          style={{ opacity: offset === 11 ? 0.35 : 1, background: C.field }} aria-label="الشهر التالي" data-testid="month-next-btn">
          <ChevronLeft size={20} />
        </button>
      </div>

      {!data && !error && <div className="flex justify-center py-16"><Loader2 className="animate-spin" /></div>}
      {error && <div className="text-center py-10" style={{ color: '#FCA5A5' }} data-testid="announcement-error">{error}</div>}

      {data && posts && (
        <>
          <Summary data={data} />
          <div className="flex gap-2 mb-5 flex-wrap" role="tablist">
            {LANGS.map((l) => (
              <button key={l.code} type="button" role="tab" aria-selected={lang === l.code} onClick={() => setLang(l.code)}
                className="rounded-full px-4 py-2 text-sm font-semibold"
                style={{ background: lang === l.code ? C.accent : C.field, color: lang === l.code ? C.accentInk : C.secondary, border: `1px solid ${C.border}`, transition: 'background-color 150ms' }}
                data-testid={`lang-tab-${l.code}`}>
                {l.label}
              </button>
            ))}
          </div>
          <p className="text-xs mb-4" style={{ color: C.muted }} data-testid="month-name-in-lang">
            اسم الشهر في هذه اللغة: <span dir={dir}>{monthName(data, lang)}</span>
          </p>

          <div className="space-y-4">
            <PlatformCard icon={Twitter} name="اكس" hint="نص قصير، والرابط يُحسب 23 حرفاً" testId="platform-x">
              <PostField label="المنشور" text={posts.x} dir={dir} testId="post-x" xLimit={280} />
            </PlatformCard>
            <PlatformCard icon={Youtube} name="يوتيوب" hint="ارفعه كمقطع قصير. أول ثلاثة وسوم تظهر فوق العنوان" testId="platform-youtube">
              <PostField label="العنوان (100 حرف كحد أقصى)" text={posts.youtube.title} dir={dir} testId="post-youtube-title" />
              <PostField label="الوصف" text={posts.youtube.description} dir={dir} testId="post-youtube-description" />
            </PlatformCard>
            <PlatformCard icon={Instagram} name="إنستغرام" hint="الروابط في الوصف لا تعمل، لذلك نشير إلى رابط الملف الشخصي" testId="platform-instagram">
              <PostField label="الوصف" text={posts.instagram} dir={dir} testId="post-instagram" />
            </PlatformCard>
            <PlatformCard icon={Pin} name="بينترست" hint="العنوان والوصف والرابط في حقول منفصلة" testId="platform-pinterest">
              <PostField label="العنوان" text={posts.pinterest.title} dir={dir} testId="post-pinterest-title" />
              <PostField label="الوصف (500 حرف كحد أقصى)" text={posts.pinterest.description} dir={dir} testId="post-pinterest-description" />
              <PostField label="الرابط" text={posts.pinterest.link} dir="ltr" testId="post-pinterest-link" />
            </PlatformCard>
            <PlatformCard icon={Linkedin} name="لنكد إن" hint="صياغة مهنية مع رابطي المتجرين" testId="platform-linkedin">
              <PostField label="المنشور" text={posts.linkedin} dir={dir} testId="post-linkedin" />
            </PlatformCard>
            <PlatformCard icon={Facebook} name="فيسبوك" hint="النص الكامل مع رابطي المتجرين والوسوم" testId="platform-facebook">
              <PostField label="المنشور" text={posts.facebook} dir={dir} testId="post-facebook" />
            </PlatformCard>
          </div>
        </>
      )}
    </div>
  );
};
